import CoreModal from 'core/CoreModal'
import { TxStatus } from 'interfaces/tx.interface'
import { ReactNode } from 'react'
import ReactLoading from 'react-loading'
import { theme } from 'theme'
import SuccessIcon from 'assets/icons-color/success.png'
import FailIcon from 'assets/icons-color/fail.png'
import { Button, Link, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { TxResult, useWallet } from '@terra-money/wallet-provider'
import HorizontalItem from 'components/container/HorizontalItem'
import OpenInFinder from 'components/OpenInFinder'
import { shortenAddress } from 'utils/display'
import openInFinder from 'utils/openInFinder'

type Props = {
  onClose: () => void
  txStatus?: TxStatus
  txError?: string
  txResult?: TxResult
}

const TxModal = ({ onClose, txStatus, txError, txResult }: Props) => {
  const {
    network: { chainID },
  } = useWallet()
  const Icon: Record<TxStatus, ReactNode> = {
    PendingApprove: (
      <Box sx={{ marginX: 'auto', marginBottom: '20px' }}>
        <ReactLoading
          type="spinningBubbles"
          color={theme.palette.primary.main}
          width={128}
          height={128}
        />
      </Box>
    ),
    PendingReceipt: (
      <Box sx={{ margin: '50px auto 20px auto' }}>
        <ReactLoading
          type="spinningBubbles"
          color={theme.palette.primary.main}
          width={128}
          height={128}
        />
      </Box>
    ),
    Success: (
      <img src={SuccessIcon} alt="SuccessIcon" width={128} height={128} />
    ),
    Failed: <img src={FailIcon} alt="FailIcon" width={128} height={128} />,
    Revert: <img src={FailIcon} alt="FailIcon" width={128} height={128} />,
  }

  const Body: Record<TxStatus, ReactNode> = {
    PendingApprove: (
      <>
        <Typography variant="h5" mb="16px">
          Waiting for Terra Station
        </Typography>
        <Typography variant="body1">
          Please confirm your transaction on wallet.
        </Typography>
        <Box sx={{ height: '24px' }} />
      </>
    ),
    PendingReceipt: (
      <>
        <Typography variant="h5" mb="16px">
          Waiting for Terra Station
        </Typography>
        <Typography variant="body2">This might take a few moments.</Typography>
        <Typography variant="body2">
          <Link
            onClick={() =>
              openInFinder({
                address: txResult?.result.txhash || '',
                type: 'tx',
                chainID,
              })
            }
          >
            View transaction
          </Link>
        </Typography>
        <Box sx={{ height: '24px' }} />
      </>
    ),
    Success: (
      <>
        <Typography variant="h5">Completed!</Typography>
        <HorizontalItem>
          <Typography variant="body1">Tx Hash</Typography>
          <Box sx={{ display: 'flex' }}>
            <Typography variant="body1" fontWeight={600}>
              {shortenAddress(txResult?.result.txhash || '')}
            </Typography>
            <OpenInFinder type="tx" address={txResult?.result.txhash || ''} />
          </Box>
        </HorizontalItem>
        <Button
          variant="contained"
          fullWidth
          onClick={onClose}
          sx={{ marginTop: '32px' }}
        >
          Okay
        </Button>
      </>
    ),
    Failed: (
      <>
        <Typography variant="h5">Failed</Typography>
        <Typography variant="body1">{txError} </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={onClose}
          sx={{ marginTop: '32px' }}
        >
          Okay
        </Button>
      </>
    ),
    Revert: (
      <>
        <Typography variant="h5">Failed</Typography>
        <Typography variant="body1">{txResult?.result.raw_log} </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={onClose}
          sx={{ marginTop: '32px' }}
        >
          Okay
        </Button>
      </>
    ),
  }

  const Content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {txStatus && Icon[txStatus]}
      </Box>
      <Box sx={{ alignItems: 'center', textAlign: 'center' }}>
        {txStatus && Body[txStatus]}
      </Box>
    </Box>
  )

  return <CoreModal open content={Content} onClose={onClose} />
}

export default TxModal
